import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ExtraDnsRecordType, SimpleExtraDnsDomain } from "src/api/generated/frontend_internal_v1";
import { useForm } from "@tanstack/react-form";
import { Dialog, DialogActions, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { Button } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { notify } from "src/components/base/toast";

/**
 * The properties for {@link LinkExtraRecordDialog}
 */
export type LinkExtraRecordPropsDialog = {
    /** The uuid of the device */
    device_ip: string | undefined;
    /** List of all available extra records */
    domains: Array<SimpleExtraDnsDomain>;
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback function when the form is submitted */
    onSubmit: () => void;
    /** Callback function when the Dialog is closed */
    onClose: () => void;
};

/**
 * Dialog to add a extra record to a device
 */
export default function LinkExtraRecordDialog(props: LinkExtraRecordPropsDialog) {
    const [t] = useTranslation("dialog-add-extra-record-device");
    const [tg] = useTranslation();

    const [query, setQuery] = React.useState("");

    const form = useForm({
        defaultValues: {
            record_type: ExtraDnsRecordType.A,
            domain: props.domains[0],
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!props.device_ip) return;
                await ApiInternal.dns.addSingleExtraDnsRecord(value.domain.uuid, {
                    record_type: value.record_type,
                    to_ip: props.device_ip,
                });
                notify.success(t("toast.success-link-domain"));
                props.onSubmit();
            },
        },
    });

    const filteredDomains =
        query === "" ? props.domains : props.domains.filter((d) => d.from_dns.includes(query.toLowerCase()));

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.device_ip, props.domains]);

    if (!props.device_ip) return null;

    return (
        <Dialog onClose={props.onClose} open={true}>
            <DialogTitle>{t("heading.add-extra-record")}</DialogTitle>
            <Form onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"domain"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.domain")}</RequiredLabel>
                                    <Combobox
                                        value={fieldApi.state.value}
                                        onChange={(e) => {
                                            if (!e) return;
                                            fieldApi.handleChange(e);
                                        }}
                                        onClose={() => setQuery("")}
                                        onQueryChange={(q) => setQuery(q)}
                                        queryDisplay={(d) => (d ? d.from_dns : "")}
                                    >
                                        {filteredDomains.map((d) => (
                                            <ComboboxOption value={d} key={d.uuid}>
                                                <Label>{d.from_dns}</Label>
                                            </ComboboxOption>
                                        ))}
                                    </Combobox>
                                </Field>
                            )}
                        </form.Field>
                        <form.Field name={"record_type"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.record-type")}</RequiredLabel>
                                    <Listbox
                                        onChange={fieldApi.handleChange}
                                        name="record-type"
                                        value={fieldApi.state.value}
                                    >
                                        <ListboxOption value={ExtraDnsRecordType.A}>
                                            <ListboxLabel>{ExtraDnsRecordType.A}</ListboxLabel>
                                        </ListboxOption>
                                        <ListboxOption value={ExtraDnsRecordType.AAAA}>
                                            <ListboxLabel>{ExtraDnsRecordType.AAAA}</ListboxLabel>
                                        </ListboxOption>
                                    </Listbox>
                                </Field>
                            )}
                        </form.Field>

                        <DialogActions>
                            <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                            <Button type={"submit"} color={"blue"}>
                                {tg("button.add")}
                            </Button>
                        </DialogActions>
                    </FieldGroup>
                </Fieldset>
            </Form>
        </Dialog>
    );
}
